import beTwMerge from '@/utils/beTwMerge';

import Button from '@/components/homepage/Button';
import DropdownButton from '@/components/homepage/DropdownButton/DropdownButton';
import { ColorKeys } from '@/styles/colors';
import ResponsiveImage, { ResponsiveImageSet } from '@/utils/ResponsiveImage';
import RichText from '@/components/RichText/RichText';
import useWistiaScript from '@/hooks/useWistiaScript';

type ActionType = 'primary' | 'secondary' | 'link';
type DropdownActionType = Exclude<ActionType, 'link'>;

export type Action = {
  label: string;
  type?: ActionType;
  color?: ColorKeys;
  onClick?: () => void;
  href?: string;
  target?: string;
  actions?: Action[];
  className?: string;
};

export default function ContentBlockLite({
  title,
  titleRichText = false,
  description,
  subHeader = '',
  isVideo = false,
  isWistia = false,
  videoSrc,
  videoClassname = 'tw-object-cover tw-w-full tw-h-full',
  videoTitle = 'Wistia video',
  autoPlay = false,
  loop = false,
  controls = false,
  hasMediaDescription = false,
  mediaDescription,
  imageSources,
  imageClassname,
  imageAlt,
  desktopMediaPosition = 'left',
  mobileMediaPosition = 'top',
  color = 'white',
  actions = [],
  classNames = '',
  richTextClass = '',
  titleClassName = '',
}: {
  title: string;
  titleRichText?: boolean;
  description: string;
  subHeader?: string;
  isVideo?: boolean;
  isWistia?: boolean;
  videoSrc?: string;
  videoClassname?: string;
  videoTitle?: string;
  autoPlay?: boolean;
  loop?: boolean;
  controls?: boolean;
  hasMediaDescription?: boolean;
  mediaDescription?: string;
  imageSources?: ResponsiveImageSet;
  imageClassname?: string;
  imageAlt?: string;
  desktopMediaPosition?: 'top' | 'bottom' | 'left' | 'right';
  mobileMediaPosition?: 'top' | 'bottom';
  color?: ColorKeys;
  actions: null | Action[];
  classNames?: string;
  richTextClass?: string;
  titleClassName?: string;
}) {
  useWistiaScript();

  const backgroundColor = {
    'light-green-light': 'tw-bg-color-light-green-light tw-text-white',
    'light-grey': 'tw-bg-color-light-grey tw-text-color-black',
    'light-green': 'tw-bg-color-light-green tw-text-white',
    'dark-rose-light': 'tw-bg-color-dark-rose-light tw-text-color-black',
    'dark-rose': 'tw-bg-color-dark-rose tw-text-color-black',
    'dark-green': 'tw-bg-color-dark-green tw-text-white',
    'dark-green-dark': 'tw-bg-color-dark-green-dark tw-text-white',
    'light-green-cta': 'tw-bg-color-light-green-cta tw-text-white',
    white: 'tw-bg-white tw-text-color-black',
    black: 'tw-bg-black tw-text-white',
    'white-grey-20': 'tw-bg-color-grey-20',
  };
  const textColor = backgroundColor[color].includes('tw-text-white') ? 'tw-text-white' : 'tw-text-color-black';

  return (
    <section
      className={beTwMerge(
        'tw-py-14 lg:tw-py-20',
        backgroundColor[color],
        color === 'white-grey-20' &&
          'tw-py-0 lg:tw-py-0 tw-my-14 lg:tw-my-20, [&:last-child]:tw-my-0 lg:[&:last-child]:tw-my-0 lg:[&:first-child]:tw-mt-0 [&:first-child]:tw-mt-0',
        classNames
      )}
    >
      <div
        className={beTwMerge(
          'contentblock--grid tw-grid tw-grid-cols-2 lg:tw-grid-cols-10 tw-max-w-[1206px] tw-mx-4 lg:tw-mx-8 tw-px-0 lg:tw-px-0 min-[1256px]:tw-px-8 min-[1256px]:tw-mx-auto tw-gap-y-8 tw-gap-x-4 lg:tw-gap-x-6',
          desktopMediaPosition === 'bottom' && 'lg:tw-grid-cols-2'
        )}
      >
        {/* Media */}
        <div
          className={beTwMerge(
            'contentblock--grid--media tw-col-span-full lg:tw-col-span-5 tw-flex tw-flex-col tw-justify-center tw-items-center',
            desktopMediaPosition === 'left' && 'lg:tw-order-1',
            desktopMediaPosition === 'right' && 'lg:tw-order-2',
            desktopMediaPosition === 'bottom' && 'lg:tw-order-2',
            mobileMediaPosition === 'top' ? 'tw-order-1' : 'tw-order-2',
            isWistia && 'lg:tw-col-span-6 lg:tw-pl-[38px]'
          )}
        >
          <div
            className={beTwMerge(
              'tw-w-full tw-flex tw-justify-center [&_picture]:tw-w-full',
              desktopMediaPosition === 'left' && 'lg:tw-max-w-[521px] lg:tw-self-start',
              desktopMediaPosition === 'right' && 'lg:tw-max-w-[521px] lg:tw-self-end',
              desktopMediaPosition === 'bottom' && 'lg:tw-self-center',
              isWistia && 'lg:tw-max-w-none'
            )}
          >
            {/* Media */}
            {/* eslint-disable-next-line no-nested-ternary */}
            {isWistia && videoSrc && (
              <div className={`${videoClassname}`}>
                {videoSrc && (
                  <iframe
                    title={videoTitle}
                    src={videoSrc}
                    scrolling="no"
                    className="wistia_embed tw-w-full tw-h-full tw-border-0 tw-overflow-hidden"
                    allowFullScreen={false}
                    width="1178"
                    height="662"
                  />
                )}
                {imageSources && (
                  <img
                    loading="lazy"
                    src={imageSources[0].src}
                    alt={imageAlt || ''}
                    className="wistia-poster tw-absolute tw-left-[0] tw-top-[0] tw-w-full tw-h-auto tw-z-[-1]"
                  />
                )}
              </div>
            )}
            {isVideo && videoSrc && (
              <video
                className={`tw-py-8 ${videoClassname}`}
                autoPlay={autoPlay}
                muted
                loop={loop}
                controls={controls}
                playsInline
              >
                <source src={videoSrc} type="video/mp4" />
              </video>
            )}

            {!videoSrc && (!isVideo || !isWistia) && (
              <ResponsiveImage
                sources={imageSources}
                alt={imageAlt}
                className={`tw-w-full tw-h-auto tw-object-cover ${imageClassname || ''}`}
              />
            )}
          </div>

          {/* Media Description */}
          {hasMediaDescription && (
            <div
              className={beTwMerge(
                'lg:tw-max-w-[521px]',
                desktopMediaPosition === 'left' ? 'lg:tw-self-start' : 'lg:tw-self-end',
                isWistia && 'lg:tw-max-w-none'
              )}
            >
              <RichText
                className={beTwMerge(
                  '[&>p:not(:last-child)]:tw-mb-4 tw-mt-8 lg:tw-mt-6 [&_p]:tw-font-avenirmedium [&_p]:!tw-text-be-sm [&_p]:lg:!tw-text-be-lg',
                  textColor
                )}
                content={mediaDescription || ''}
              />
            </div>
          )}
        </div>

        {/* Content */}
        <div
          className={beTwMerge(
            'contentblock--grid--content tw-flex tw-flex-col tw-justify-center tw-col-span-full lg:tw-col-span-5 tw-mx-auto',
            desktopMediaPosition === 'left' ? 'lg:tw-order-2' : 'lg:tw-order-1',
            mobileMediaPosition === 'top' ? 'tw-order-2' : 'tw-order-1',
            isWistia && 'lg:tw-col-span-4',
            textColor
          )}
        >
          {titleRichText ? (
            <h2
              className={beTwMerge(
                'tw-text-left tw-text-[24px] lg:tw-text-[52px] tw-mb-4 md:tw-mb-6 tw-mt-0 [&>span.mark]:tw-text-lg [&>span.mark]:tw-align-top',
                textColor,
                titleClassName
              )}
              dangerouslySetInnerHTML={{ __html: title }}
            />
          ) : (
            <h2
              className={beTwMerge(
                'tw-text-left tw-text-[24px] lg:tw-text-[52px] tw-mb-4 lg:tw-mb-[30px] tw-mt-0',
                textColor,
                titleClassName
              )}
            >
              {title}
            </h2>
          )}

          <RichText
            className={beTwMerge(
              '[&>p:not(:last-child)]:tw-mb-4 [&_p]:tw-font-avenirmedium [&_p]:!tw-text-be-sm [&_p]:lg:!tw-text-be-lg [&_a]:!tw-text-be-sm [&_a]:lg:!tw-text-be-lg [&>p]:!tw-leading-[1.35em]',
              textColor,
              richTextClass
            )}
            content={description}
          />

          {actions && actions.length > 0 && (
            <div className="tw-font-avenirmedium tw-flex tw-flex-col md:tw-flex-row md:tw-justify-start md:tw-gap-x-2 tw-mt-8 tw-gap-y-2 tw-justify-center tw-items-center tw-w-full">
              {actions?.slice(0, 2).map((action, index) => {
                if (action.actions) {
                  const { actions: subActions } = action;
                  return (
                    <DropdownButton
                      key={action.label.split(' ').join('-')}
                      label={action.label}
                      options={subActions}
                      color={action.color}
                      type={action.type as DropdownActionType}
                      className={beTwMerge(
                        'tw-max-w-[768px]',
                        actions.length === index + 1 && 'tw-mb-0',
                        action.className
                      )}
                    />
                  );
                }
                const { href } = action;
                return (
                  <Button
                    key={action.label.split(' ').join('-')}
                    className={beTwMerge(
                      'tw-max-w-[768px]',
                      actions.length === index + 1 && 'tw-mb-0',
                      action.className
                    )}
                    label={action.label}
                    ariaLabel={!titleRichText ? `${title} - ${action.label}` : action.label}
                    color={action.color}
                    type={action.type}
                    href={href}
                  />
                );
              })}
            </div>
          )}

          {subHeader && (
            <span className="tw-font-avenirmedium tw-mt-5 tw-text-be-sm lg:tw-text-[15px]">{subHeader}</span>
          )}
        </div>
      </div>
    </section>
  );
}
